import React, { useState, useCallback } from "react";
import { Button, Form, Nav } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { financeAuthPayload } from "../../components/finance/AccountSelect";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * Financial Statements (Phase C)
 *
 * Three read-only reports derived from the general ledger:
 *  - Balance Sheet (as of a date): Assets = Liabilities + Equity
 *  - Income Statement (period): Revenue − Expenses = Net Profit/Loss
 *  - Cash Flow (period): opening + net movement = closing
 *
 * Backend: reports/balance-sheet, reports/income-statement, reports/cash-flow.
 * Line-item arrays are rendered defensively — the report still shows its
 * headline totals even if the backend omits the detail breakdown.
 */

const today = () => new Date().toISOString().slice(0, 10);
const yearStart = () => `${new Date().getFullYear()}-01-01`;

// Pull the first present array from a set of candidate keys.
const pickArray = (obj, keys) => {
  for (const k of keys) {
    if (Array.isArray(obj?.[k])) return obj[k];
  }
  return [];
};

const TABS = [
  { key: "balance", label: "Balance Sheet" },
  { key: "income", label: "Income Statement" },
  { key: "cash", label: "Cash Flow" },
];

const FinancialStatements = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [tab, setTab] = useState("balance");
  const [asOfDate, setAsOfDate] = useState(today());
  const [fromDate, setFromDate] = useState(yearStart());
  const [toDate, setToDate] = useState(today());

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const money = (v) => `${customer_currency} ${formatNumber(v || 0)}`;

  const run = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      let url;
      let body;
      if (tab === "balance") {
        url = ApiConfig.balanceSheet;
        body = { ...financeAuthPayload(), as_of_date: asOfDate };
      } else if (tab === "income") {
        url = ApiConfig.incomeStatement;
        body = { ...financeAuthPayload(), from_date: fromDate, to_date: toDate };
      } else {
        url = ApiConfig.cashFlowStatement;
        body = { ...financeAuthPayload(), from_date: fromDate, to_date: toDate };
      }
      const res = await simplePostCall(url, JSON.stringify(body));
      if (res?.result) {
        setData(res.data || {});
      } else {
        setData(null);
        toast.error(res?.message || "Failed to load report.");
      }
    } catch (err) {
      console.error("Report error:", err);
      toast.error("An error occurred loading the report.");
    } finally {
      setLoading(false);
    }
  }, [tab, asOfDate, fromDate, toDate]);

  const switchTab = (key) => {
    setTab(key);
    setData(null);
  };

  return (
    <div className="finance-page-container">
      <ToastContainer />

      <div className="special-header panel-white-all">
        <h4 className="page-title">Financial Statements</h4>
      </div>

      <div className="yc-list-body">
        <Nav variant="tabs" activeKey={tab} className="fin-report-tabs mb-3">
          {TABS.map((t) => (
            <Nav.Item key={t.key}>
              <Nav.Link eventKey={t.key} onClick={() => switchTab(t.key)}>
                {t.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {/* Controls */}
        <div className="yc-list-filter">
          {tab === "balance" ? (
            <Form.Control
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              title="As of date"
            />
          ) : (
            <>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                title="From date"
              />
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                title="To date"
              />
            </>
          )}
          <Button className="btn btn-primary" onClick={run} disabled={loading}>
            {loading ? "Loading..." : "Generate"}
          </Button>
        </div>

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : !data ? (
          <div className="fin-empty">
            <span className="fin-empty-title">No report generated yet</span>
            <span className="fin-empty-text">
              Choose a {tab === "balance" ? "date" : "date range"} and click
              Generate. Reports are built live from posted journal entries.
            </span>
          </div>
        ) : tab === "balance" ? (
          <BalanceSheet data={data} money={money} />
        ) : tab === "income" ? (
          <IncomeStatement data={data} money={money} />
        ) : (
          <CashFlow data={data} money={money} />
        )}
      </div>
    </div>
  );
};

/* ---------- Balance Sheet ---------- */
function BalanceSheet({ data, money }) {
  const assets = pickArray(data, ["assets", "asset_accounts"]);
  const liabilities = pickArray(data, ["liabilities", "liability_accounts"]);
  const equity = pickArray(data, ["equity", "equity_accounts"]);
  const balanced = data.is_balanced;

  return (
    <>
      <div className="fin-stat-row">
        <div className="fin-stat-card fin-accent-asset">
          <span className="fin-stat-label">Total Assets</span>
          <span className="fin-stat-value">{money(data.total_assets)}</span>
        </div>
        <div className="fin-stat-card fin-accent-liability">
          <span className="fin-stat-label">Total Liabilities</span>
          <span className="fin-stat-value">{money(data.total_liabilities)}</span>
        </div>
        <div className="fin-stat-card fin-accent-equity">
          <span className="fin-stat-label">Total Equity</span>
          <span className="fin-stat-value">{money(data.total_equity)}</span>
        </div>
        <div
          className={`fin-stat-card ${
            balanced ? "fin-accent-income" : "fin-accent-expense"
          }`}
        >
          <span className="fin-stat-label">Balance Check</span>
          <span className="fin-stat-value">
            {balanced ? "Balanced" : "Out of Balance"}
          </span>
          <span className="fin-stat-sub">A = L + E</span>
        </div>
      </div>

      <ReportSection title="Assets" rows={assets} total={data.total_assets} money={money} />
      <ReportSection
        title="Liabilities"
        rows={liabilities}
        total={data.total_liabilities}
        money={money}
      />
      <ReportSection title="Equity" rows={equity} total={data.total_equity} money={money} />
    </>
  );
}

/* ---------- Income Statement ---------- */
function IncomeStatement({ data, money }) {
  const income = pickArray(data, ["income", "revenue", "income_accounts"]);
  const expenses = pickArray(data, ["expenses", "expense", "expense_accounts"]);
  const profit = Number(data.net_income) || 0;

  return (
    <>
      <div className="fin-stat-row">
        <div className="fin-stat-card fin-accent-income">
          <span className="fin-stat-label">Total Income</span>
          <span className="fin-stat-value">{money(data.total_income)}</span>
        </div>
        <div className="fin-stat-card fin-accent-expense">
          <span className="fin-stat-label">Total Expenses</span>
          <span className="fin-stat-value">{money(data.total_expense)}</span>
        </div>
        <div
          className={`fin-stat-card ${
            profit >= 0 ? "fin-accent-income" : "fin-accent-expense"
          }`}
        >
          <span className="fin-stat-label">
            {profit >= 0 ? "Net Profit" : "Net Loss"}
          </span>
          <span className="fin-stat-value">{money(Math.abs(profit))}</span>
        </div>
      </div>

      <ReportSection title="Income" rows={income} total={data.total_income} money={money} />
      <ReportSection
        title="Expenses"
        rows={expenses}
        total={data.total_expense}
        money={money}
      />
    </>
  );
}

/* ---------- Cash Flow ---------- */
function CashFlow({ data, money }) {
  const movements = pickArray(data, ["movements", "lines", "activities"]);

  return (
    <>
      <div className="fin-stat-row">
        <div className="fin-stat-card fin-accent-all">
          <span className="fin-stat-label">Opening Cash</span>
          <span className="fin-stat-value">
            {money(data.opening_cash_balance)}
          </span>
        </div>
        <div
          className={`fin-stat-card ${
            Number(data.net_cash_flow) >= 0
              ? "fin-accent-income"
              : "fin-accent-expense"
          }`}
        >
          <span className="fin-stat-label">Net Cash Flow</span>
          <span className="fin-stat-value">{money(data.net_cash_flow)}</span>
        </div>
        <div className="fin-stat-card fin-accent-asset">
          <span className="fin-stat-label">Closing Cash</span>
          <span className="fin-stat-value">
            {money(data.closing_cash_balance)}
          </span>
        </div>
      </div>

      {movements.length > 0 && (
        <ReportSection
          title="Cash Movements"
          rows={movements}
          money={money}
          amountKey="amount"
          labelKey="label"
        />
      )}
    </>
  );
}

/* ---------- Shared line-item section ---------- */
function ReportSection({
  title,
  rows,
  total,
  money,
  amountKey = "balance",
  labelKey,
}) {
  if (!rows || rows.length === 0) {
    return (
      <>
        <div className="fin-section-head">
          <span className="fin-section-title">{title}</span>
        </div>
        <div className="yc-list-row-list">
          <div className="yc-list-row-card">
            <div className="yc-list-cell text-muted">
              No detail lines returned.
              {total != null && (
                <>
                  {" "}
                  Total: <strong>{money(total)}</strong>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="fin-section-head">
        <span className="fin-section-title">{title}</span>
        <span className="fin-section-count">
          {rows.length} {rows.length === 1 ? "line" : "lines"}
        </span>
      </div>
      <div className="yc-list-row-list">
        {rows.map((r, i) => {
          const amount =
            r[amountKey] ??
            r.amount ??
            r.balance ??
            r.value ??
            0;
          const label =
            (labelKey && r[labelKey]) ||
            [r.account_code, r.account_name].filter(Boolean).join(" · ") ||
            r.label ||
            r.name ||
            "—";
          return (
            <div key={i} className="yc-list-row-card">
              <div className="yc-list-grid yc-list-grid-2">
                <div className="yc-list-cell">{label}</div>
                <div className="yc-list-cell fw-semibold">{money(amount)}</div>
              </div>
            </div>
          );
        })}
        {total != null && (
          <div className="yc-list-row-card fin-subtotal-row">
            <div className="yc-list-grid yc-list-grid-2">
              <div className="yc-list-cell fw-bold">Total {title}</div>
              <div className="yc-list-cell fw-bold">{money(total)}</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default FinancialStatements;
